import { useState } from 'react';
import clsx from 'clsx';
import { AssetTypesEnum } from '@types';
import { ReactComponent as SearchIcon } from '@assets/icons/search_icon.svg';
import {
  BlockingCheck,
  getBlockingCheckGroups,
  getBlockingChecksForAsset,
  matchesBlockingCheckSearch
} from './blockingChecks';

type Props = {
  assetType: AssetTypesEnum;
  selectedCheckIds: string[];
  onChange: (checkIds: string[]) => void;
};

const uniqueIds = (ids: string[]): string[] => Array.from(new Set(ids));

const CheckToggle = ({
  check,
  checked,
  onToggle
}: {
  check: BlockingCheck;
  checked: boolean;
  onToggle: () => void;
}) => {
  const label = `${check.name} ${check.conditionLabel}`;

  return (
    <div
      className={clsx(
        'flex cursor-pointer items-center justify-between gap-4 px-4 py-3 hover:bg-grey-50',
        checked ? 'bg-grey-50' : 'bg-white'
      )}
      onClick={onToggle}>
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <span
          className={clsx(
            'text-sm',
            checked ? 'font-semibold text-grey-900' : 'font-normal text-grey-700'
          )}>
          {check.name}
        </span>
        <span className="flex-shrink-0 rounded-md bg-[#FDEEF4] px-2.5 py-1 text-xs font-medium text-[#8A2B52]">
          {check.conditionLabel}
        </span>
      </div>
      <button
        type="button"
        role="switch"
        aria-label={label}
        aria-checked={checked}
        onClick={(event) => {
          event.stopPropagation();
          onToggle();
        }}
        className={clsx(
          'relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2',
          checked ? 'bg-secondary' : 'bg-grey-200'
        )}>
        <span
          aria-hidden="true"
          className={clsx(
            'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
            checked ? 'translate-x-4' : 'translate-x-0'
          )}
        />
      </button>
    </div>
  );
};

const BlockingChecksSelector = ({
  assetType,
  selectedCheckIds,
  onChange
}: Props) => {
  const [searchTerm, setSearchTerm] = useState('');
  const availableChecks = getBlockingChecksForAsset(assetType);
  const groups = getBlockingCheckGroups(assetType);
  const selectedSet = new Set(selectedCheckIds);
  const selectedCount = availableChecks.filter((check) =>
    selectedSet.has(check.id)
  ).length;
  const filteredGroups = groups
    .map((group) => ({
      ...group,
      filteredChecks: group.checks.filter((check) =>
        matchesBlockingCheckSearch(check, searchTerm)
      )
    }))
    .filter((group) => group.filteredChecks.length > 0);

  const toggleCheck = (checkId: string) => {
    onChange(
      selectedSet.has(checkId)
        ? selectedCheckIds.filter((id) => id !== checkId)
        : uniqueIds([...selectedCheckIds, checkId])
    );
  };

  const toggleGroup = (checks: BlockingCheck[]) => {
    const groupIds = checks.map((check) => check.id);
    const allSelected = groupIds.every((id) => selectedSet.has(id));

    onChange(
      allSelected
        ? selectedCheckIds.filter((id) => !groupIds.includes(id))
        : uniqueIds([...selectedCheckIds, ...groupIds])
    );
  };

  return (
    <div className="overflow-hidden rounded-lg border border-grey-200 bg-white">
      <div className="flex flex-col gap-3 border-b border-grey-200 px-4 py-3 md:flex-row md:items-center">
        <div className="relative min-w-0 flex-1">
          <SearchIcon className="absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-grey-400" />
          <input
            type="search"
            aria-label="Search blocking checks"
            placeholder="Search blocking checks"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            className="w-full rounded-md border border-grey-300 py-2 pl-9 pr-3 text-sm text-grey-900 placeholder:text-grey-400 focus:border-secondary focus:outline-none"
          />
        </div>
        <div className="flex flex-shrink-0 items-center justify-between gap-4 md:justify-end">
          <span className="text-[12px] font-semibold text-grey-700">
            {selectedCount} of {availableChecks.length} checks blocking
          </span>
          <button
            type="button"
            disabled={selectedCount === 0}
            onClick={() => onChange([])}
            className="rounded-md border border-grey-300 px-3 py-1.5 text-sm font-medium text-grey-500 hover:bg-grey-50 disabled:cursor-not-allowed disabled:opacity-50">
            Clear all
          </button>
        </div>
      </div>

      {filteredGroups.map((group) => {
        const selectedInGroup = group.checks.filter((check) =>
          selectedSet.has(check.id)
        ).length;
        const allSelected = group.filteredChecks.every((check) =>
          selectedSet.has(check.id)
        );

        return (
          <section
            key={group.id}
            aria-labelledby={`${group.id}-heading`}
            className="border-b border-grey-200 last:border-b-0">
            <div className="flex items-center justify-between gap-4 px-4 pb-2 pt-4">
              <h3
                id={`${group.id}-heading`}
                className="text-xs font-bold uppercase tracking-wide text-secondary">
                {group.label}{' '}
                <span className="pl-2 font-normal normal-case text-grey-400">
                  {selectedInGroup} of {group.checks.length}
                </span>
              </h3>
              <button
                type="button"
                onClick={() => toggleGroup(group.filteredChecks)}
                className="flex-shrink-0 text-sm font-semibold text-secondary hover:text-secondary-dark hover:underline">
                {allSelected ? 'Turn all off' : 'Turn all on'}
              </button>
            </div>

            <div className="pb-2">
              {group.filteredChecks.map((check) => (
                <CheckToggle
                  key={check.id}
                  check={check}
                  checked={selectedSet.has(check.id)}
                  onToggle={() => toggleCheck(check.id)}
                />
              ))}
            </div>
          </section>
        );
      })}
      {filteredGroups.length === 0 && (
        <p className="px-4 py-8 text-center text-sm text-grey-500">
          No blocking checks found.
        </p>
      )}
    </div>
  );
};

export default BlockingChecksSelector;
